/**
 * AI Use Case Detail — read-only document view of a use case in the AI Use Case Register.
 *
 * Ported 1:1 from the FAIR app's AI Governance module (src/pages/ai/AiUseCaseDetail.tsx),
 * then extended with the EU AI Act blocks. This is the "document" a reviewer reads: the risk
 * classification, the business case, the expected outcomes, the data/privacy position, the
 * oversight and vendor position, and the decision that was taken on it. Editing happens in
 * the intake wizard — everything here is display, plus the status state machine.
 *
 * Two identity lookups happen on this page and they resolve against **different registers**,
 * which is the trap that produced a raw `USER-…` id on the Meeting Minutes detail page
 * (MeetingMinutesDetail.tsx:760 puts an `approvedBy` userId through a personId-keyed map, so
 * the lookup never hits and it prints the id):
 *   - `humanOversight.accountablePersonId` is a **personId** → the People register.
 *   - `decision.decidedBy` is a **userId** (`USER-…`), stamped from the caller's JWT → the
 *     Users register (`GET /users`, readable by any authenticated user).
 * Each has its own map here, and each falls back to an honestly-labelled raw id rather than
 * printing a bare code as if it were a name.
 */

import React, { useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { NimbusModal, NimbusModalContent } from '../../components/NimbusModal';
import { isRichTextEmpty } from '../../components/RichTextField';
import {
  aiUseCaseApi, aiUseCaseReviewApi, peopleApi, usersApi, departmentsApi, reviewAssignmentsApi, documentApi,
  parseApiError,
} from '../../services/api';
import { RESIDUAL_RISK_LABELS, RESIDUAL_RISK_TONE } from '../../constants/privacy';
import type { ResidualRiskLevel } from '../../constants/privacy';
import type {
  AiType, AiUseCaseStatus, TransferMechanism, VendorRecord, CoverageVerdict, AiUseCaseAuditEvent,
} from '../../types/models';
import {
  AI_TYPE_LABELS, AI_TYPE_ICONS, AI_TYPE_TONE,
  AI_STATUS_LABELS, AI_STATUS_TONE, AI_STATUS_TRANSITIONS, AI_STATUS_ACTION_LABELS, AI_STATUS_PENDING_LABELS,
  LEGAL_BASIS_OPTIONS, DATA_CATEGORY_LABELS, SENSITIVITY_LABELS, SENSITIVITY_TONE,
  RACI_ROLES, dataFieldRiskCell,
} from '../../constants/aiGovernance';
import {
  AI_ACT_CLASSIFICATION_LABELS, AI_ACT_CLASSIFICATION_TONE, AI_ACT_CLASSIFICATION_ICONS,
  AI_ACT_CLASSIFICATION_DESCRIPTIONS,
  ACT_ROLE_LABELS, ACT_ROLE_TONE, ACT_ROLE_ICONS, ACT_ROLE_DESCRIPTIONS,
  GPAI_TIER_LABELS, GPAI_TIER_TONE, GPAI_TIER_ICONS, GPAI_TIER_DESCRIPTIONS,
  ADM_FLAGS, HUMAN_OVERSIGHT_CONTROLS, MONITORING_FIELDS,
  REVIEW_FREQUENCY_LABELS,
  APPROVAL_DECISION_LABELS, APPROVAL_DECISION_TONE, APPROVAL_DECISION_ICONS,
  TRANSFER_MECHANISM_LABELS, TRANSFER_MECHANISM_TONE, hasUnlawfulTransfer,
  DPIA_VERDICT_LABELS, DPIA_VERDICT_TONE,
  emptyHumanOversight, emptyMonitoring,
  gpaiTier, isBlocking, isClassified, hasAnyOversight, needsAutomatedDecisionDetails,
} from '../../constants/aiAct';
import { LinkedArtefacts } from '../../components/LinkedArtefacts';
import { DocumentManager } from '../../components/DocumentManager';
import { formatDate } from '../../utils/format';
import { describeApiError } from '../../utils/apiError';
import { useToast } from '../../hooks/useToast';
import { useSwal } from '../../hooks/useSwal';
import { useAuth } from '../../hooks/useAuth';
import { aiUseCaseKeys, invalidateAiUseCase, writeAiUseCase } from './aiUseCaseKeys';

// ─── Review tab types ──────────────────────────────────────────────────────────
// Generic module (ReviewAssignment) and the Bedrock review's own result shape - both
// loosely typed by design at the API boundary (services/api.ts), narrowed here to what
// this page actually reads.

interface ReviewAssignment {
  assignmentId: string;
  scope: 'primary' | 'field';
  fieldKey?: string;
  note?: string;
  assignedToUserId?: string;
  status: 'unassigned' | 'assigned' | 'complete';
}

// Only the fields the Bedrock review pass ever suggests (see _sanitize_drafted_fields in
// lambda/prod-nimbus-ai-use-cases/lambda_function.py) - a human-friendly label instead of
// the raw camelCase attribute name on a screen a reviewer actually reads.
// Every key _sanitize_drafted_fields can send (lambda/prod-nimbus-ai-use-cases/lambda_function.py's
// closed_vocab/free_text_fields/boolean_fields) needs an entry here - anything missing falls back
// to the raw camelCase attribute name (isGpai, aiType, ...), which is what produced the
// inconsistent-looking mix of proper labels and raw field names in this list.
const DRAFTED_FIELD_LABELS: Record<string, string> = {
  legalBasis: 'Legal basis',
  processingRationale: 'Processing rationale',
  aiActClassification: 'AI Act classification',
  classificationRationale: 'Classification rationale',
  actRole: 'Role in the value chain',
  dataRetention: 'Data retention',
  aiType: 'AI type',
  summary: 'Summary',
  thirdPartyDetails: 'Third-party details',
  businessValue: 'Business value',
  alternatives: 'Alternatives considered',
  isGpai: 'General-purpose AI (GPAI)',
  isGpaiSystemic: 'GPAI with systemic risk',
};

interface AiUseCaseReviewResult {
  status: 'generating' | 'complete' | 'failed';
  error?: string;
  precedentMatches?: { name: string; why: string }[];
  piiDisagreements?: { dataFieldName: string; why: string }[];
  inferredDataGaps?: { dataUsage: string; why: string }[];
  humanInvestigationChecklist?: string[];
  keyRisks?: { risk: string; why: string }[];
  draftedFields?: Record<string, { value: string; rationale: string; confidence: string }>;
  // The one output that decides what Approve actually does - link an existing Processing
  // Activity/Subprocessor, draft a new one, or create a DPIA. Previously computed and stored
  // but never rendered anywhere; now the basis for the consequence banner, the Approve
  // confirmation, and the Compliance Package card.
  coverageVerdict?: CoverageVerdict;
  // Stamped server-side when the review was requested, from the use case's own updatedAt at
  // that moment - the only way to tell "this review covers the current content" from "this is
  // stale, left over from a prior reject -> reopen -> edit -> resubmit cycle". '' on a review
  // written before this field existed, which counts as current (no baseline to compare against).
  reviewedUseCaseUpdatedAt?: string;
}

/** Plain-language consequences of approving, derived from the coverage verdict - the exact
 *  list the confirmation step shows before Approve fires, and the same list the consequence
 *  banner above the tabs summarises. Empty means "nothing further" - a real, expected result
 *  for a use case that's already fully covered by existing registers. */
const describeApprovalConsequences = (coverage: CoverageVerdict | undefined): string[] => {
  if (!coverage) return [];
  const lines: string[] = [];
  const pa = coverage.processingActivity;
  if (pa?.covered && pa.matchedId) lines.push(`Link to existing Processing Activity ${pa.matchedId}`);
  else if (pa?.needsNew) lines.push('Create a new Processing Activity (draft)');
  const sp = coverage.subprocessor;
  if (sp?.covered && sp.matchedId) lines.push(`Link to existing Subprocessor ${sp.matchedId}`);
  else if (sp?.needsNew) lines.push('Create a new Subprocessor (draft)');
  if (coverage.dpiaNeeded) lines.push('Create a DPIA');
  if (coverage.aiModuleCompliancePageRelevant) lines.push('Flag for the AI Module Compliance Page');
  return lines;
};

/** Plain-language rendering of one audit event's `details` blob - raw JSON here (e.g.
 *  `{"aiType":"","name":"..."}` for a CREATE) was flagged as unreadable in real use. Each
 *  event type's detail shape is fixed server-side (see the log_audit call sites in
 *  prod-nimbus-ai-use-cases/lambda_function.py), so this is a plain switch rather than a
 *  generic formatter - a new event type falls back to the raw JSON rather than showing
 *  nothing, so a gap here is visible instead of silently swallowed. */
const humanizeKey = (key: string): string =>
  key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();

const describeAuditEvent = (ev: AiUseCaseAuditEvent): string => {
  const d = ev.details || {};
  switch (ev.eventType) {
    case 'CREATE': {
      const aiType = d.aiType ? AI_TYPE_LABELS[d.aiType as AiType] || d.aiType : '';
      return `Created "${(d.name as string) || 'Untitled'}"${aiType ? ` (${aiType})` : ''}`;
    }
    case 'UPDATE': {
      const keys = Array.isArray(d.changedKeys) ? (d.changedKeys as string[]) : [];
      const bySystem = d.source === 'ai-review-auto-apply' ? ' by the AI review' : '';
      return keys.length > 0
        ? `Updated ${keys.map(humanizeKey).join(', ')}${bySystem}`
        : `Updated${bySystem} (no field changes recorded)`;
    }
    case 'DELETE': {
      const status = d.status ? AI_STATUS_LABELS[d.status as AiUseCaseStatus] || d.status : '';
      return `Deleted "${(d.name as string) || 'Untitled'}"${status ? ` (was ${status})` : ''}`;
    }
    case 'TRANSITION': {
      const from = d.fromStatus ? AI_STATUS_LABELS[d.fromStatus as AiUseCaseStatus] || d.fromStatus : '?';
      const to = d.toStatus ? AI_STATUS_LABELS[d.toStatus as AiUseCaseStatus] || d.toStatus : '?';
      const decision = d.decision ? ` — decision: ${d.decision}` : '';
      return `Moved from ${from} to ${to}${decision}`;
    }
    case 'APPROVED_WITHOUT_REVIEW':
      return `Approved without a completed review — reason given: "${(d.reason as string) || '(none)'}"`;
    case 'DRAFTING_FAILED':
      return `Approval-time document drafting failed${d.retry ? ' (on retry)' : ''}: ${(d.error as string) || 'unknown error'}`;
    case 'DRAFTING_RETRIED':
      return 'Drafting was retried';
    case 'AUTO_ASSIGN_FAILED':
      return `Could not auto-assign a reviewer: ${(d.error as string) || 'unknown error'}`;
    case 'AUTO_REVIEW_FAILED':
      return `Could not auto-start the review: ${(d.error as string) || 'unknown error'}`;
    default:
      return Object.keys(d).length > 0 ? JSON.stringify(d) : '—';
  }
};

// ─── Small display primitives ─────────────────────────────────────────────────

const Dash: React.FC = () => <span className="np-sub">—</span>;

const NotProvided: React.FC<{ label?: string }> = ({ label = 'Not provided' }) => (
  <span className="np-sub fst-italic">{label}</span>
);

/** Read-only field caption. There is no np- class for this, so it composes np-hint. */
const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="np-hint fw-semibold text-uppercase mt-0 mb-1" style={{ letterSpacing: '0.05em' }}>
    {children}
  </div>
);

const LabelValue: React.FC<{ label: string; value?: React.ReactNode; hint?: string }> = ({ label, value, hint }) => (
  <div>
    <FieldLabel>{label}</FieldLabel>
    <div>{value || <NotProvided />}</div>
    {hint && <p className="np-hint">{hint}</p>}
  </div>
);

/**
 * A ReactQuill field. An "empty" editor still round-trips as '<p><br></p>', which the
 * source rendered as a blank box — strip the tags first and show "Not provided" instead.
 */
const HtmlValue: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="mb-4">
    <FieldLabel>{label}</FieldLabel>
    {isRichTextEmpty(value)
      ? <NotProvided />
      : <div dangerouslySetInnerHTML={{ __html: value }} />}
  </div>
);

const SectionCard: React.FC<{
  title: string;
  icon: string;
  /** Rendered as direct children of np-card-head, which right-aligns np-btn / np-chip. */
  actions?: React.ReactNode;
  /** Zero the body padding so a table can sit edge-to-edge in the card. */
  flush?: boolean;
  children: React.ReactNode;
}> = ({ title, icon, actions, flush = false, children }) => (
  <div className="np-card mb-4">
    <div className="np-card-head">
      <h2><i className={icon}></i> {title}</h2>
      {actions}
    </div>
    <div className="np-card-body" style={flush ? { padding: 0 } : undefined}>{children}</div>
  </div>
);

/** A yes/no governance answer. "No" is a real answer here, so it is stated, not blanked. */
const YesNo: React.FC<{ value: boolean; yesTone?: string; noTone?: string }> = ({
  value, yesTone = 'success', noTone = 'neutral',
}) => (
  <span className={`np-chip np-tone-${value ? yesTone : noTone}`}>
    <i className={value ? 'ri-check-line' : 'ri-close-line'}></i>
    {value ? 'Yes' : 'No'}
  </span>
);

/** A plain-text value that may contain newlines. Never `dangerouslySetInnerHTML`: the
 *  monitoring and escalation fields are stored as plain text and are not sanitised as HTML. */
const TextBlock: React.FC<{ value: string }> = ({ value }) => (
  value.trim()
    ? <div style={{ whiteSpace: 'pre-wrap' }}>{value}</div>
    : <NotProvided />
);

// ─── Status transitions ───────────────────────────────────────────────────────
// Three tiers of ceremony, by what the move costs:
//   nothing        — submit, a routine forward step
//   swal.confirm   — retire and return-to-draft, which lose something but record nothing
//   a real form    — approve and reject, which WRITE a decision. `swal.confirm` returns only
//                    a boolean, so it physically cannot capture the reason the server demands
//                    on a rejection (400 without one) or the conditions on an approval.
// `active` and `rejected` are absent from this map on purpose, not by omission — they are
// keyed in DECISION_FORM below instead, and handleTransition checks that first.
const TRANSITION_CONFIRM: Partial<Record<AiUseCaseStatus, { title: string; text: string }>> = {
  retired: {
    title: 'Retire this use case?',
    text: 'Retired is the final status — a retired use case cannot be moved back to active. Retire it once the AI has been switched off or decommissioned.',
  },
  draft: {
    title: 'Return this use case to draft?',
    text: 'It will be withdrawn from review and will need to be submitted again. Any recorded decision is cleared from the record — the audit trail keeps its own copy.',
  },
};

const TRANSITION_ICON: Record<AiUseCaseStatus, string> = {
  draft: 'ri-arrow-go-back-line',
  submitted: 'ri-send-plane-line',
  approved: 'ri-checkbox-circle-line',
  rejected: 'ri-close-circle-line',
  active: 'ri-checkbox-circle-line',
  retired: 'ri-archive-line',
};

const TRANSITION_BTN_CLASS: Record<AiUseCaseStatus, string> = {
  draft: 'np-btn np-btn-ghost',
  submitted: 'np-btn np-btn-primary',
  approved: 'np-btn np-btn-primary',
  rejected: 'np-btn np-btn-ghost-danger',
  active: 'np-btn np-btn-primary',
  retired: 'np-btn np-btn-ghost-danger',
};

/**
 * Statuses whose records the API refuses to update at all (`writable_for_status` in the
 * Lambda returns nothing for these, and a PUT comes back 409). Written out rather than derived
 * from `AI_STATUS_TRANSITIONS`, because "can it move on" and "can it be edited" are different
 * questions that happen to have the same answer for `retired`.
 */
const READ_ONLY_STATUSES = new Set<AiUseCaseStatus>(['rejected', 'retired']);

/** Copy for the decision form. Keyed by the two transitions that stamp a decision. Approving
 * lands on 'approved', not 'active' — 'active' means "fully compliant" and is only ever
 * reached by the backend's own automatic promotion once the compliance checklist is
 * satisfied, never a client-requested transition (see AI_STATUS_TRANSITIONS). */
const DECISION_FORM: Partial<Record<AiUseCaseStatus, {
  title: string;
  icon: string;
  intro: string;
  label: string;
  hint: string;
  placeholder: string;
  /** A rejection without a reason is refused by the API, so the button stays disabled. */
  textRequired: boolean;
  submitLabel: string;
  submitClass: string;
}>> = {
  approved: {
    title: 'Approve this use case',
    icon: 'ri-checkbox-circle-line',
    intro: 'Approving records you as the approver, with a timestamp, against this version of the record. It becomes Active once its compliance checklist (DPIA, Subprocessor, Processing Activity, Privacy Policy) is fully satisfied — immediately if nothing further is needed, otherwise as each item completes.',
    label: 'Conditions (optional)',
    hint: 'Anything that has to be true for this approval to hold — a control to be in place, a review date, a limit on scope. Leaving it empty records a clean approval; filling it in records an approval with conditions.',
    placeholder: 'e.g. output review stays in place for the first 90 days; re-approve before extending to other departments…',
    textRequired: false,
    submitLabel: 'Approve',
    submitClass: 'np-btn np-btn-primary',
  },
  rejected: {
    title: 'Reject this use case',
    icon: 'ri-close-circle-line',
    intro: 'A rejection is not a dead end — the use case can be returned to draft, revised and resubmitted, and it has to pass every gate again on the way through.',
    label: 'Reason for rejection',
    hint: 'Required. This is what the owner will work from, and it is the part of the decision that survives in the audit trail after the record is reopened.',
    placeholder: 'e.g. no human able to override an individual outcome, and no accountable person named for a system that decides customer eligibility.',
    textRequired: true,
    submitLabel: 'Reject',
    submitClass: 'np-btn np-btn-danger',
  },
};

/**
 * Days until a bare `YYYY-MM-DD` date, or null if there isn't one. Local midnight on both
 * sides, matching `daysBetween` in `CollectionScheduleCard.tsx:24-33`, because the platform's
 * existing due-date semantics are "before today as the reader sees today". No interval
 * arithmetic happens on this page — the wizard owns that — so the UTC `addInterval` rules do
 * not come into it.
 */
const daysUntil = (dateStr: string): number | null => {
  if (!dateStr) return null;
  const target = new Date(dateStr);
  if (isNaN(target.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / 86400000);
};

/** A date chip that says whether the date has already gone by. Used for review and contract
 *  dates, where "in 3 days" and "6 weeks ago" need to read differently at a glance. */
const DueChip: React.FC<{ date: string; pastLabel: string; soonDays?: number }> = ({
  date, pastLabel, soonDays = 30,
}) => {
  const days = daysUntil(date);
  if (days === null) return <NotProvided label="Not set" />;
  const tone = days < 0 ? 'danger' : days <= soonDays ? 'warning' : 'neutral';
  const suffix = days < 0 ? ` · ${pastLabel}` : days === 0 ? ' · today' : days <= soonDays ? ` · in ${days}d` : '';
  return (
    <span className={`np-chip np-tone-${tone} np-tabular`}>
      {formatDate(date)}{suffix}
    </span>
  );
};

/** A vendor row is worth showing even when only the name was filled in. */
const vendorIsEmpty = (v: VendorRecord): boolean =>
  !v.name.trim() && !v.modelName.trim() && !v.hostingRegion.trim()
  && !v.dpaReference.trim() && !v.contractExpiry.trim() && !v.subProcessors.trim();

// ─── Page ─────────────────────────────────────────────────────────────────────

export const AiUseCaseDetail: React.FC = () => {
  const { useCaseId } = useParams<{ useCaseId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const swal = useSwal();

  /** Which decision form is open, if any. Null closes it. */
  const [decisionTarget, setDecisionTarget] = useState<AiUseCaseStatus | null>(null);
  const [decisionText, setDecisionText] = useState('');
  /** Only used when approving without a completed review - a typed, audit-logged override
   *  the server requires in that exact case (see reviewOverrideReason in the transition API). */
  const [overrideReasonText, setOverrideReasonText] = useState('');

  const [activeTab, setActiveTab] = useState<'overview' | 'attachments' | 'artefacts' | 'review' | 'audit'>('overview');
  const { user } = useAuth();
  // Must match App.tsx's isTenantAdmin expression verbatim.
  const isTenantAdmin = (user?.role || '').toLowerCase() === 'admin';
  const currentUserId = user?.userId || '';

  const { data: uc, isLoading, isError } = useQuery({
    queryKey: aiUseCaseKeys.detail(useCaseId),
    queryFn: () => aiUseCaseApi.get(useCaseId!),
    enabled: !!useCaseId,
  });

  // ── Identity resolution — two registers, two maps. See the file header. ──
  // `accountablePersonId` is a personId. The People query is already the most-cached query in
  // the app, so this is normally a cache read, but it is still only asked for when there is an
  // id to resolve.
  const accountablePersonId = uc?.humanOversight?.accountablePersonId || '';
  // RACI roles marked personPicker (owner, raciResponsible, raciInfoSec, raciLegal) are also
  // real People-register foreign keys now (see Step2Identity.tsx) - resolved through the same
  // query as accountablePersonId rather than a second fetch.
  const raciPersonIds = useMemo(
    () => RACI_ROLES.filter((r) => r.personPicker).map((r) => uc?.[r.field]).filter((id): id is string => !!id),
    [uc],
  );
  const { data: people = [] } = useQuery({
    queryKey: ['people'],
    queryFn: peopleApi.getAll,
    enabled: !!accountablePersonId || raciPersonIds.length > 0,
  });
  // The real People-register records carry firstName/lastName (confirmed live against the
  // DynamoDB table) - personName is only a legacy/fallback shape, not what's actually stored.
  type PersonLike = { personId?: string; firstName?: string; lastName?: string; personName?: string };
  const getPersonLabel = (p: PersonLike): string => {
    const full = `${p.firstName || ''} ${p.lastName || ''}`.trim();
    return (full || p.personName || '').trim();
  };
  const personName = useMemo(() => {
    if (!accountablePersonId) return '';
    const match = (people as PersonLike[]).find((p) => p.personId === accountablePersonId);
    return match ? getPersonLabel(match) : '';
  }, [people, accountablePersonId]);
  // A record written before this field became a real foreign key has a plain free-text name in
  // this slot; it won't match any real personId, so this resolves to '' and the role falls back
  // to "Not set" rather than a match found by accident - same tradeoff already accepted for
  // departmentId below.
  const personNameFor = (id: string): string => {
    if (!id) return '';
    const match = (people as PersonLike[]).find((p) => p.personId === id);
    return match ? getPersonLabel(match) : '';
  };

  // `decidedBy` is a userId (USER-…) from the caller's verified token, which the People
  // register does not contain. `GET /users` is readable by any authenticated user.
  // Always enabled (not just when decidedBy is set) - the Review tab's reviewer picker
  // needs the full list regardless of whether a decision has been recorded yet.
  const decidedBy = uc?.decidedBy || '';
  const { data: usersResponse } = useQuery<{ users?: Array<{ userId?: string; name?: string; email?: string; role?: string }> }>({
    queryKey: ['users'],
    queryFn: () => usersApi.getAll(),
  });
  const deciderName = useMemo(() => {
    if (!decidedBy) return '';
    const match = (usersResponse?.users || []).find((u) => u.userId === decidedBy);
    return match?.name || match?.email || '';
  }, [usersResponse, decidedBy]);

  // `departmentId` is a real foreign key into the Departments register (see Step2Identity.tsx).
  // A record written before that change has a plain department NAME in this slot; it won't
  // match any real id, so this resolves to '' and the meta row falls back to "Not set" rather
  // than a match found by accident.
  const departmentId = uc?.departmentId || '';
  const { data: departments = [] } = useQuery<Array<{ departmentId?: string; name?: string }>>({
    queryKey: ['departments'],
    queryFn: departmentsApi.getAll,
    enabled: !!departmentId,
  });
  const departmentName = useMemo(() => {
    if (!departmentId) return '';
    return departments.find((d) => d.departmentId === departmentId)?.name || '';
  }, [departments, departmentId]);

  // ── Review tab: generic assignment module + the Bedrock review pass ──
  const { data: assignmentsResp } = useQuery<{ assignments: ReviewAssignment[] }>({
    queryKey: ['reviewAssignments', 'ai_use_case', uc?.useCaseId],
    queryFn: () => reviewAssignmentsApi.listForEntity('ai_use_case', uc!.useCaseId),
    enabled: !!uc?.useCaseId,
  });
  const assignments = assignmentsResp?.assignments || [];
  const primaryAssignment = assignments.find((a) => a.scope === 'primary');
  const fieldAssignments = assignments.filter((a) => a.scope === 'field');
  const usersList = usersResponse?.users || [];
  const nameForUserId = (id: string) => usersList.find((u) => u.userId === id)?.name
    || usersList.find((u) => u.userId === id)?.email || id;
  const emailForUserId = (id: string) => usersList.find((u) => u.userId === id)?.email || '';

  const { data: reviewResp } = useQuery<{ review: AiUseCaseReviewResult }>({
    queryKey: ['aiUseCaseReview', uc?.useCaseId],
    queryFn: () => aiUseCaseReviewApi.get(uc!.useCaseId),
    enabled: !!uc?.useCaseId,
    refetchInterval: (query) => (query.state.data?.review?.status === 'generating' ? 3000 : false),
    retry: false,
  });
  const review = reviewResp?.review;

  // Residual risk is owned entirely by the linked DPIA document - fetched read-time here
  // rather than mirrored server-side onto the use case, so this stays in sync without a new
  // cross-lambda IAM grant for a value that's only ever displayed, never acted on, from here.
  const { data: linkedDpiaDoc } = useQuery<{ residualRiskLevel?: ResidualRiskLevel }>({
    queryKey: ['document', uc?.linkedDpiaId],
    queryFn: async () => {
      const data = await documentApi.getOne(uc!.linkedDpiaId);
      return (data as any).document || data;
    },
    enabled: !!uc?.linkedDpiaId,
  });
  const linkedDpiaResidualRisk = linkedDpiaDoc?.residualRiskLevel;

  const reviewComplete = review?.status === 'complete';
  // Mirrors the server's own _review_is_current: complete alone isn't enough - a review left
  // over from before the record's last edit (a reject -> reopen -> edit -> resubmit cycle
  // whose own re-review never completed, or never even started - e.g. the spend cap was hit)
  // must not silently satisfy the gate against content nobody has actually reviewed.
  const reviewStale = reviewComplete && !!review?.reviewedUseCaseUpdatedAt && review.reviewedUseCaseUpdatedAt !== uc?.updatedAt;
  const reviewCurrent = reviewComplete && !reviewStale;

  // Audit tab - only fetched once that tab is opened, since the events themselves never
  // affect anything else on this page.
  const { data: auditResp, isLoading: auditLoading } = useQuery<{ events: AiUseCaseAuditEvent[] }>({
    queryKey: ['aiUseCaseAudit', uc?.useCaseId],
    queryFn: () => aiUseCaseApi.getAudit(uc!.useCaseId),
    enabled: !!uc?.useCaseId && activeTab === 'audit',
  });
  const auditEvents = auditResp?.events || [];

  const startReviewMutation = useMutation({
    mutationFn: () => aiUseCaseReviewApi.start(uc!.useCaseId),
    onSuccess: () => {
      toast.success('Review started — this runs in the background.');
      queryClient.invalidateQueries({ queryKey: ['aiUseCaseReview', uc?.useCaseId] });
    },
    onError: (e: Error) => toast.error(`Couldn't start the review: ${e.message}`),
  });

  const assignMutation = useMutation({
    mutationFn: ({ assignmentId, assignedToUserId, create }: { assignmentId?: string; assignedToUserId: string; create?: { scope: string; fieldKey?: string; note?: string } }) =>
      assignmentId
        ? reviewAssignmentsApi.update(assignmentId, { assignedToUserId })
        : reviewAssignmentsApi.create({ entityType: 'ai_use_case', entityId: uc!.useCaseId, assignedToUserId, ...create }),
    onSuccess: () => {
      toast.success('Reviewer updated.');
      queryClient.invalidateQueries({ queryKey: ['reviewAssignments', 'ai_use_case', uc?.useCaseId] });
    },
    onError: (e: Error) => toast.error(`Couldn't update the assignment: ${e.message}`),
  });

  const completeAssignmentMutation = useMutation({
    mutationFn: (assignmentId: string) => reviewAssignmentsApi.update(assignmentId, { status: 'complete' }),
    onSuccess: () => {
      toast.success('Marked complete.');
      queryClient.invalidateQueries({ queryKey: ['reviewAssignments', 'ai_use_case', uc?.useCaseId] });
    },
    onError: (e: Error) => toast.error(`Couldn't mark this complete: ${e.message}`),
  });

  const transitionMutation = useMutation({
    mutationFn: ({ status, text, reviewOverrideReason }: { status: AiUseCaseStatus; text?: string; reviewOverrideReason?: string }) =>
      aiUseCaseApi.transition(useCaseId!, status, text, reviewOverrideReason),
    onSuccess: (updated, { status }) => {
      // The transition response is the server's own merged view of the record, so it is
      // authoritative — write it rather than invalidating the detail key and refetching what we
      // were just handed. The lists are a different matter: a status change moves a register row
      // and every dashboard tile, and none of that is in this response.
      writeAiUseCase(queryClient, updated);
      invalidateAiUseCase(queryClient);
      setDecisionTarget(null);
      setDecisionText('');
      setOverrideReasonText('');
      toast.success(`Use case moved to ${AI_STATUS_LABELS[status]}.`);
    },
    // The API explains itself — a blocked activation returns the AI Act reason, and a
    // 413/409 returns which attributes were refused. `error.message` alone would show the
    // raw status line and drop all of it.
    //
    // 503/504 is different: approval commonly runs a slow Bedrock drafting chain server-side
    // (see _orchestrate_approval_drafting), which can outlast API Gateway's hard 29s
    // integration timeout even after the Lambda has already committed the transition and
    // gone on to succeed. Confirmed live on AI-048: the browser saw a 503 for an approval
    // that had, in fact, fully completed (status + linked DPIA/PA/Subprocessor all written).
    // Rather than show a scary error for a request that may have already worked, re-fetch the
    // real record and only fall back to the error toast if it genuinely didn't reach the
    // target status.
    onError: async (error: Error, { status }) => {
      const { status: httpStatus } = parseApiError(error);
      if ((httpStatus === 503 || httpStatus === 504) && useCaseId) {
        try {
          const refreshed = await aiUseCaseApi.get(useCaseId);
          if (refreshed.status === status) {
            writeAiUseCase(queryClient, refreshed);
            invalidateAiUseCase(queryClient);
            setDecisionTarget(null);
            setDecisionText('');
            setOverrideReasonText('');
            toast.success(`Use case moved to ${AI_STATUS_LABELS[status]}.`);
            return;
          }
        } catch {
          // Fall through to the normal error toast below.
        }
      }
      toast.error(describeApiError(error, 'Failed to update status.'));
    },
  });

  const retryDraftingMutation = useMutation({
    mutationFn: () => aiUseCaseApi.retryDrafting(uc!.useCaseId),
    onSuccess: (updated) => {
      writeAiUseCase(queryClient, updated);
      toast.success('Drafting retried.');
    },
    onError: (error: Error) => toast.error(describeApiError(error, 'Retry failed.')),
  });

  const handleTransition = async (status: AiUseCaseStatus) => {
    if (DECISION_FORM[status]) {
      setDecisionText('');
      setOverrideReasonText('');
      setDecisionTarget(status);
      return;
    }
    const confirmCopy = TRANSITION_CONFIRM[status];
    if (confirmCopy && !(await swal.confirm(confirmCopy.title, confirmCopy.text))) return;
    transitionMutation.mutate({ status });
  };

  if (isLoading) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y np-page">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (isError || !uc) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y np-page">
        <PageHeader
          icon="ri-robot-line"
          title="Use case not found"
          subtitle={`No AI use case could be loaded for ${useCaseId ?? 'this reference'}.`}
          breadcrumbs={[{ label: 'AI Use Cases', to: '/ai-use-cases' }, { label: 'Not found' }]}
          actions={
            <button className="np-btn np-btn-ghost" onClick={() => navigate('/ai-use-cases')}>
              <i className="ri-arrow-left-line"></i>
              Back to register
            </button>
          }
        />
        <div className="np-banner np-tone-danger" role="alert">
          <i className="ri-error-warning-line"></i>
          This use case may have been deleted, or you may not have access to it.
        </div>
      </div>
    );
  }

  const aiType = uc.aiType ? (uc.aiType as AiType) : null;
  const transitions = AI_STATUS_TRANSITIONS[uc.status] ?? [];
  // 'customer_controlled' counts — a non-empty string is truthy regardless of value, so this
  // has to compare against 'no' explicitly rather than test isPersonalData for truthiness.
  const hasPersonalData = uc.dataFields.some((f) => f.isPersonalData !== 'no');
  const legalBasis = LEGAL_BASIS_OPTIONS.find((o) => o.value === uc.legalBasis);
  const wizardPath = (step: number) => `/ai-use-cases/${uc.useCaseId}/intake/${step}`;

  // ── AI Act view of the record ──
  // Coalesced at the read boundary, as the port plan requires: every record written before
  // these fields existed reads them as undefined, and this page dereferences them unguarded.
  // The Lambda hydrates too — this is the second belt, and it costs nothing.
  const classification = uc.aiActClassification || '';
  const classDescription = AI_ACT_CLASSIFICATION_DESCRIPTIONS[classification];
  const actRole = uc.actRole || '';
  const gpai = gpaiTier({ isGpai: !!uc.isGpai, isGpaiSystemic: !!uc.isGpaiSystemic });
  const oversight = uc.humanOversight ?? emptyHumanOversight();
  const monitoring = uc.monitoring ?? emptyMonitoring();
  const vendors = (uc.vendors ?? []).filter((v) => !vendorIsEmpty(v));
  const transfers = uc.transfers ?? [];
  const decision = uc.decision || '';
  const admFlagged = needsAutomatedDecisionDetails({
    makesAutomatedDecisions: !!uc.makesAutomatedDecisions,
  });
  const prohibited = isBlocking(classification);
  const oversightCount = HUMAN_OVERSIGHT_CONTROLS.filter((c) => oversight[c.field]).length;
  const oversightMissing = classification === 'high_risk' && !hasAnyOversight(oversight);
  const unlawfulTransfer = hasUnlawfulTransfer(transfers);
  const monitoringRecorded = MONITORING_FIELDS.some(({ field }) => (monitoring[field] || '').trim());
  const hasMonitoringOrReview = monitoringRecorded || !!uc.reviewFrequency || !!uc.nextReviewDate;
  const reviewDays = daysUntil(uc.nextReviewDate || '');
  const decisionForm = decisionTarget ? DECISION_FORM[decisionTarget] : undefined;
  // Approve is gated on a completed review server-side (transition_use_case refuses it
  // otherwise) - the same rule is enforced here so the button doesn't just bounce off a 400.
  // A typed override reason is the one way through, and it is itself required non-empty.
  const needsReviewOverride = decisionTarget === 'approved' && !reviewCurrent;
  const decisionTextReady = (!decisionForm?.textRequired || !!decisionText.trim())
    && (!needsReviewOverride || !!overrideReasonText.trim());
  const approvalConsequences = describeApprovalConsequences(review?.coverageVerdict);

  /** Both identity lookups degrade the same way: name if we can resolve it, otherwise the raw
   *  id with a label that admits what it is. Never the bare code passed off as a name. */
  const identity = (resolved: string, rawId: string, kind: 'person' | 'user'): React.ReactNode => {
    if (!rawId) return <NotProvided label="Not recorded" />;
    if (resolved) return <span className="fw-semibold">{resolved}</span>;
    return (
      <span>
        <span className="np-tabular">{rawId}</span>{' '}
        <span className="np-hint">
          ({kind === 'person' ? 'person' : 'user'} record not found — showing the stored id)
        </span>
      </span>
    );
  };

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon="ri-robot-line"
        colour={aiType ? AI_TYPE_TONE[aiType] : 'primary'}
        title={uc.name || 'Untitled use case'}
        subtitle={uc.summary || undefined}
        breadcrumbs={[{ label: 'AI Use Cases', to: '/ai-use-cases' }, { label: uc.useCaseId }]}
        actions={
          <>
            {/* Retired is terminal — AI_STATUS_TRANSITIONS.retired is empty, so nothing renders. */}
            {transitions.map((s) => {
              // Approve/Reject route through the decision modal below rather than mutating
              // directly (see handleTransition), so this only ever fires true for the
              // transitions that DO mutate straight from this button (submit, retire, return
              // to draft) — but it's keyed off the mutation's own variables rather than
              // hardcoded to those statuses, so it stays correct if that ever changes.
              const pending = transitionMutation.isPending && transitionMutation.variables?.status === s;
              return (
                <button
                  key={s}
                  className={TRANSITION_BTN_CLASS[s]}
                  onClick={() => handleTransition(s)}
                  disabled={transitionMutation.isPending}
                >
                  {pending
                    ? <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                    : <i className={TRANSITION_ICON[s]}></i>}
                  {pending ? AI_STATUS_PENDING_LABELS[s] : AI_STATUS_ACTION_LABELS[s]}
                </button>
              );
            })}
            {/* A rejected or retired record is read-only server-side — every attribute is
                frozen and a PUT comes back 409. Sending someone into the wizard to find that
                out one save later is worse than saying so here. `approved`/`active` stay
                editable because they are only partly frozen (department, owner, RACI,
                oversight, vendors, transfers, monitoring and the review dates remain
                writable). */}
            <button
              className="np-btn np-btn-ghost"
              onClick={() => navigate(wizardPath(1))}
              disabled={READ_ONLY_STATUSES.has(uc.status)}
              title={READ_ONLY_STATUSES.has(uc.status)
                ? `A ${AI_STATUS_LABELS[uc.status].toLowerCase()} use case cannot be edited. Return it to draft first.`
                : undefined}
            >
              <i className="ri-edit-line"></i>
              Edit
            </button>
            <button className="np-btn np-btn-ghost" onClick={() => navigate('/ai-use-cases')}>
              <i className="ri-arrow-left-line"></i>
              Back
            </button>
          </>
        }
      />

      <div className="d-flex align-items-center gap-2 flex-wrap mb-4">
        <span className="np-chip np-tone-neutral np-tabular">{uc.useCaseId}</span>
        {aiType && (
          <span className={`np-chip np-tone-${AI_TYPE_TONE[aiType]}`}>
            <i className={AI_TYPE_ICONS[aiType]}></i>
            {AI_TYPE_LABELS[aiType]}
          </span>
        )}
        {/* The risk tier is the organising fact about this record, so it sits in the chip row
            even when it is unanswered — an absent chip would read as "no risk". */}
        <span className={`np-chip np-tone-${AI_ACT_CLASSIFICATION_TONE[classification]}`}>
          <i className={AI_ACT_CLASSIFICATION_ICONS[classification]}></i>
          {AI_ACT_CLASSIFICATION_LABELS[classification]}
        </span>
        {/* GPAI is a separate axis, so it is a separate chip and only shown when it is true —
            "not general-purpose AI" is not a fact worth a chip. */}
        {gpai !== 'none' && (
          <span className={`np-chip np-tone-${GPAI_TIER_TONE[gpai]}`}>
            <i className={GPAI_TIER_ICONS[gpai]}></i>
            {GPAI_TIER_LABELS[gpai]}
          </span>
        )}
        <span className={`np-chip np-tone-${AI_STATUS_TONE[uc.status]}`}>{AI_STATUS_LABELS[uc.status]}</span>
        {decision && (
          <span className={`np-chip np-tone-${APPROVAL_DECISION_TONE[decision]}`}>
            <i className={APPROVAL_DECISION_ICONS[decision]}></i>
            {APPROVAL_DECISION_LABELS[decision]}
          </span>
        )}
      </div>

      {/* A prohibited practice is not a risk to be managed and not a completeness problem, so
          it is stated before the document rather than inside it. The server refuses both
          submission and approval while this tier stands — the banner is the explanation for a
          button that will not work, not a warning the user can dismiss by trying harder. */}
      {prohibited && (
        <div className="np-banner np-tone-danger mb-4" role="alert">
          <i className="ri-forbid-2-line"></i>
          <span>
            <strong>Classified as a prohibited practice under {classDescription.article}.</strong>{' '}
            {classDescription.detail} This use case cannot be submitted for review or approved
            while it stands. Either the classification is wrong — change it in{' '}
            <Link to={wizardPath(6)}>step 6</Link> and record why — or the use case does not go
            ahead.
          </span>
        </div>
      )}

      {uc.status === 'rejected' && (
        <div className="np-banner np-tone-danger mb-4" role="alert">
          <i className="ri-close-circle-line"></i>
          <span>
            <strong>This use case was rejected.</strong>{' '}
            {uc.decisionConditions
              ? uc.decisionConditions
              : 'No reason was recorded on the record — see the audit trail.'}{' '}
            Return it to draft to revise and resubmit; it will have to pass every gate again.
          </span>
        </div>
      )}

      {/* The one output that decides what Approve actually does, made visible to the person
          about to click it - previously computed and stored on every review but never rendered
          anywhere. Shown once a review exists to render, whatever the current status, so a
          submitted-but-not-yet-reviewed record makes clear that Approve is gated. */}
      {!prohibited && uc.status === 'submitted' && (
        <div className={`np-banner np-tone-${reviewCurrent ? 'info' : 'warning'} mb-4`}>
          <i className={reviewCurrent ? 'ri-git-merge-line' : 'ri-time-line'}></i>
          {reviewCurrent ? (
            <div>
              <strong>Approving this will:</strong>
              {approvalConsequences.length > 0 ? (
                <ul className="mb-0">
                  {approvalConsequences.map((line, i) => <li key={i}>{line}</li>)}
                </ul>
              ) : (
                <p className="mb-0">Nothing further — existing coverage already accounts for this use case.</p>
              )}
            </div>
          ) : reviewStale ? (
            <span>
              <strong>The latest AI review is out of date.</strong> This use case was edited
              since that review ran, so it does not reflect the current content — run a fresh
              review from the <button type="button" className="btn btn-link p-0 align-baseline" onClick={() => setActiveTab('review')}>Review tab</button>,
              or provide a typed override when approving.
            </span>
          ) : (
            <span>
              <strong>No completed AI review yet.</strong> Approve is disabled until a review
              completes — run one from the <button type="button" className="btn btn-link p-0 align-baseline" onClick={() => setActiveTab('review')}>Review tab</button>,
              or provide a typed override when approving.
            </span>
          )}
        </div>
      )}

      {/* ── Tab navigation ── */}
      <div className="np-tabs" style={{ overflowX: 'auto', flexWrap: 'nowrap' }}>
        <button
          type="button"
          className={`np-tab d-flex align-items-center gap-1${activeTab === 'overview' ? ' active' : ''}`}
          style={{ whiteSpace: 'nowrap' }}
          onClick={() => setActiveTab('overview')}
        >
          <i className="ri-file-text-line"></i>Overview
        </button>
        <button
          type="button"
          className={`np-tab d-flex align-items-center gap-1${activeTab === 'review' ? ' active' : ''}`}
          style={{ whiteSpace: 'nowrap' }}
          onClick={() => setActiveTab('review')}
        >
          <i className="ri-shield-check-line"></i>Review
          {fieldAssignments.some((a) => a.status !== 'complete') && (
            <span className="np-chip np-tone-warning">{fieldAssignments.filter((a) => a.status !== 'complete').length}</span>
          )}
        </button>
        <button
          type="button"
          className={`np-tab d-flex align-items-center gap-1${activeTab === 'attachments' ? ' active' : ''}`}
          style={{ whiteSpace: 'nowrap' }}
          onClick={() => setActiveTab('attachments')}
        >
          <i className="ri-attachment-2"></i>Attachments
        </button>
        <button
          type="button"
          className={`np-tab d-flex align-items-center gap-1${activeTab === 'artefacts' ? ' active' : ''}`}
          style={{ whiteSpace: 'nowrap' }}
          onClick={() => setActiveTab('artefacts')}
        >
          <i className="ri-links-line"></i>Linked Artefacts
        </button>
        <button
          type="button"
          className={`np-tab d-flex align-items-center gap-1${activeTab === 'audit' ? ' active' : ''}`}
          style={{ whiteSpace: 'nowrap' }}
          onClick={() => setActiveTab('audit')}
        >
          <i className="ri-history-line"></i>Audit
        </button>
      </div>

      {activeTab === 'overview' && (
      <div className="np-detail-layout">
        {/* ── Meta rail ──────────────────────────────────────────────────────
            Each RACI role carries its description, which makes this rail taller than
            the viewport on shorter screens — sticky positioning would strand its lower
            half off-screen, so this one scrolls with the page instead. */}
        <div className="np-meta-rail" style={{ position: 'static' }}>
          <div className="np-card">
            <div className="np-card-head">
              <h2><i className="ri-information-line"></i> Details</h2>
            </div>
            <div className="np-card-body">
              <div className="np-meta-row">
                <span className="np-meta-label"><i className="ri-building-line"></i>Department</span>
                <span className="np-meta-value">{departmentName || <NotProvided label="Not set" />}</span>
              </div>
              <div className="np-meta-row" style={{ alignItems: 'flex-start' }}>
                <span className="np-meta-label"><i className="ri-user-line"></i>Submitted by</span>
                {uc.createdBy ? (
                  <span className="np-meta-value" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.3 }}>
                    <span>{nameForUserId(uc.createdBy)}</span>
                    {emailForUserId(uc.createdBy) && (
                      <span className="np-sub fw-normal" style={{ wordBreak: 'break-all' }}>{emailForUserId(uc.createdBy)}</span>
                    )}
                  </span>
                ) : <span className="np-meta-value"><NotProvided label="Not recorded" /></span>}
              </div>
              <div className="np-meta-row">
                <span className="np-meta-label"><i className="ri-calendar-line"></i>Created</span>
                <span className="np-meta-value np-tabular">{formatDate(uc.createdAt)}</span>
              </div>
              <div className="np-meta-row">
                <span className="np-meta-label"><i className="ri-history-line"></i>Last updated</span>
                <span className="np-meta-value np-tabular">{formatDate(uc.updatedAt)}</span>
              </div>
              {/* DPIA screening state, written by the screening path and never by this page or
                  the wizard. Shown here rather than in the document body because it is a fact
                  *about* the record, not a section of it — the same shape the ROPA reports. */}
              <div className="np-meta-row">
                <span className="np-meta-label"><i className="ri-shield-check-line"></i>DPIA</span>
                <span className="np-meta-value">
                  <span className={`np-chip np-tone-${DPIA_VERDICT_TONE[uc.dpiaVerdict || '']}`}>
                    {DPIA_VERDICT_LABELS[uc.dpiaVerdict || '']}
                  </span>
                </span>
              </div>
              {/* linkedDpiaId/linkedProcessingActivityId/linkedSubprocessorId now live in the
                  Compliance Package card (Review tab) instead of here - that card also shows
                  the ones that are still pending or failed, which a flat "only if set" row
                  here never could. */}
              {uc.lastScreenedAt && (
                <div className="np-meta-row">
                  <span className="np-meta-label"><i className="ri-scan-line"></i>Last screened</span>
                  <span className="np-meta-value np-tabular">{formatDate(uc.lastScreenedAt)}</span>
                </div>
              )}
              {/* Links only — each risk already appears in this record's own Linked Artefacts
                  tab via the bidirectional link created when it was raised. */}
              {(uc.aiRiskAssessmentIds || []).length > 0 && (
                <div className="np-meta-row">
                  <span className="np-meta-label">
                    <i className="ri-shield-flash-line"></i>
                    AI Risk Assessment{uc.aiRiskAssessmentIds.length > 1 ? 's' : ''}
                  </span>
                  <span className="np-meta-value d-flex flex-wrap gap-2">
                    {uc.aiRiskAssessmentIds.map((riskId) => (
                      <Link key={riskId} className="np-tabular" to={`/risks/${riskId}`}>{riskId}</Link>
                    ))}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="np-card">
            <div className="np-card-head">
              <h2><i className="ri-team-line"></i> Roles &amp; Accountability</h2>
            </div>
            <div className="np-card-body">
              <div className="np-list">
                {RACI_ROLES.map((role) => (
                  <div key={role.field} className="np-list-item" style={{ alignItems: 'flex-start' }}>
                    <div>
                      <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                        <span className={`np-chip np-tone-${role.tone}`}>{role.raci}</span>
                        <span className="np-sub">{role.label}</span>
                      </div>
                      <div className="fw-semibold" style={{ fontSize: '0.828rem' }}>
                        {(role.personPicker ? personNameFor(uc[role.field]) : uc[role.field]) || <NotProvided label="Not set" />}
                      </div>
                      <p className="np-hint">{role.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── Document body ─────────────────────────────────────────────────── */}
        <div>
          {/* Classification comes first because it frames everything after it: the same
              business case reads differently depending on whether the system decides who gets
              a loan or drafts internal copy. It is also the only block whose value changes what
              the server will let you do with the record. */}
          {/* No tier chip in the card head: it is already in the page's chip row and again as
              the first field below, and three of the same chip in one viewport is noise. */}
          <SectionCard
            title="AI Act Classification"
            icon={AI_ACT_CLASSIFICATION_ICONS[classification]}
          >
            {!isClassified(classification) && (
              <div className="np-banner np-tone-warning mb-4" role="alert">
                <i className="ri-question-line"></i>
                <span>
                  <strong>Not yet classified.</strong> This is not the same as minimal risk — the
                  question has not been answered, so nothing downstream can rely on it. Classify
                  it in <Link to={wizardPath(6)}>step 6</Link>.
                </span>
              </div>
            )}

            <div className="row g-4">
              <div className="col-12 col-md-6">
                <FieldLabel>Risk tier</FieldLabel>
                <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                  <span className={`np-chip np-tone-${AI_ACT_CLASSIFICATION_TONE[classification]}`}>
                    <i className={AI_ACT_CLASSIFICATION_ICONS[classification]}></i>
                    {AI_ACT_CLASSIFICATION_LABELS[classification]}
                  </span>
                  {classDescription.article !== '—' && (
                    <span className="np-chip np-tone-neutral">{classDescription.article}</span>
                  )}
                </div>
                <p className="np-hint mb-0">{classDescription.short}</p>
              </div>

              <div className="col-12 col-md-6">
                <FieldLabel>Our role in the value chain</FieldLabel>
                <div className="mb-1">
                  <span className={`np-chip np-tone-${ACT_ROLE_TONE[actRole]}`}>
                    <i className={ACT_ROLE_ICONS[actRole]}></i>
                    {ACT_ROLE_LABELS[actRole]}
                  </span>
                </div>
                <p className="np-hint mb-0">{ACT_ROLE_DESCRIPTIONS[actRole].short}</p>
              </div>

              <div className="col-12 col-md-6">
                <FieldLabel>General-purpose AI</FieldLabel>
                <div className="mb-1">
                  <span className={`np-chip np-tone-${GPAI_TIER_TONE[gpai]}`}>
                    <i className={GPAI_TIER_ICONS[gpai]}></i>
                    {GPAI_TIER_LABELS[gpai]}
                  </span>
                  {GPAI_TIER_DESCRIPTIONS[gpai].article !== '—' && (
                    <span className="np-chip np-tone-neutral ms-2">{GPAI_TIER_DESCRIPTIONS[gpai].article}</span>
                  )}
                </div>
                {/* Spelled out because the two axes look like one on screen: a system can be
                    high-risk AND general-purpose, and neither answer implies the other. */}
                <p className="np-hint mb-0">
                  {GPAI_TIER_DESCRIPTIONS[gpai].short} A separate question from the risk tier above.
                </p>
              </div>

              <div className="col-12 col-md-6">
                <FieldLabel>Decisions about people</FieldLabel>
                {/* The question is printed as asked, from ADM_FLAGS — no shortened restatement
                    of it here. A hand-written label per field would also silently mislabel any
                    third flag added to that array. */}
                <div className="np-list">
                  {ADM_FLAGS.map((flag) => (
                    <div key={flag.field} className="np-list-item" style={{ alignItems: 'flex-start' }}>
                      <div>
                        <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                          <YesNo value={!!uc[flag.field]} yesTone={flag.tone} />
                          <span className="fw-semibold" style={{ fontSize: '0.828rem' }}>
                            <i className={`${flag.icon} me-1`}></i>
                            {flag.label}
                          </span>
                        </div>
                        {/* The consequence is shown once the answer is yes, not as scare copy
                            up front — the same rule the wizard step follows. */}
                        {!!uc[flag.field] && <p className="np-hint mb-0">{flag.trigger}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {admFlagged && (
                <div className="col-12">
                  <LabelValue
                    label="What is decided, about whom"
                    value={uc.automatedDecisionDetails ? <TextBlock value={uc.automatedDecisionDetails} /> : undefined}
                    hint="Required once the decisions answer above is yes — GDPR Art 22 and an Annex III determination both turn on it. Profiling on its own does not require it; it feeds the DPIA instead."
                  />
                </div>
              )}
            </div>

            <hr className="np-divider" />
            <HtmlValue label="Why this classification" value={uc.classificationRationale} />
            <p className="np-hint mb-0">
              The tier on its own is not defensible — this is the reasoning behind it.
              {classification === 'high_risk' && ' The server requires it before a high-risk use case can be submitted.'}
            </p>
          </SectionCard>

          <SectionCard title="Business Case" icon="ri-briefcase-line">
            <HtmlValue label="Problem Being Solved" value={uc.businessProblem} />
            <HtmlValue label="Expected Value" value={uc.businessValue} />
            <div className="mb-0">
              <FieldLabel>Alternatives Considered</FieldLabel>
              {isRichTextEmpty(uc.alternatives)
                ? <NotProvided />
                : <div dangerouslySetInnerHTML={{ __html: uc.alternatives }} />}
            </div>
          </SectionCard>

          {uc.outcomes.length > 0 && (
            <SectionCard title="Expected Outcomes" icon="ri-target-line" flush>
              <div className="np-table-wrap">
                <table className="np-table">
                  <thead>
                    <tr>
                      <th>Outcome</th>
                      <th>How Measured</th>
                      <th>Target Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uc.outcomes.map((o) => (
                      <tr key={o.id}>
                        <td className="np-row-title">{o.outcome || <Dash />}</td>
                        <td>{o.metric || <Dash />}</td>
                        <td className="np-tabular">{o.targetDate ? formatDate(o.targetDate) : <Dash />}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionCard>
          )}

          {uc.dataFields.length > 0 && (
            <SectionCard title="Data & Privacy" icon="ri-shield-user-line">
              <div className="np-table-wrap">
                <table className="np-table">
                  <thead>
                    <tr>
                      <th>Data Field</th>
                      <th>Category</th>
                      <th>Personal?</th>
                      <th>Sensitivity</th>
                      <th>Data Source</th>
                      <th>Purpose</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uc.dataFields.map((f) => {
                      const personal = dataFieldRiskCell(f);
                      return (
                        <tr key={f.id}>
                          <td className="np-row-title">{f.name || <Dash />}</td>
                          <td>{DATA_CATEGORY_LABELS[f.category]}</td>
                          <td>
                            <span className={`np-chip np-tone-${personal.tone}`}>{personal.text}</span>
                          </td>
                          <td>
                            <span className={`np-chip np-tone-${SENSITIVITY_TONE[f.sensitivity]}`}>
                              {SENSITIVITY_LABELS[f.sensitivity]}
                            </span>
                          </td>
                          <td>{f.dataSource || <Dash />}</td>
                          <td>{f.processingPurpose || <Dash />}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* The GDPR position only has to be answered once any field is personal data. */}
              {hasPersonalData && (
                <>
                  <hr className="np-divider" />
                  <div className="row g-4">
                    <div className="col-12 col-md-6">
                      <LabelValue
                        label="Legal Basis"
                        value={legalBasis?.label ?? uc.legalBasis}
                        hint={legalBasis?.description}
                      />
                    </div>
                    <div className="col-12 col-md-6">
                      <LabelValue label="Data Retention" value={uc.dataRetention} />
                    </div>
                    <div className="col-12">
                      <LabelValue label="Processing Rationale" value={uc.processingRationale} />
                    </div>
                    {uc.thirdPartySharing && (
                      <div className="col-12">
                        <div className="np-banner np-tone-warning" role="alert">
                          <i className="ri-alert-line"></i>
                          <span>
                            <strong>Third-party sharing:</strong>{' '}
                            {uc.thirdPartyDetails || <NotProvided label="No detail provided" />}
                            {/* This free-text field is superseded by the structured vendor list
                                below, but is never migrated or hidden: existing records hold
                                real prose here and dropping it would lose it. */}
                            {vendors.length > 0 && (
                              <span className="np-hint d-block mt-1">
                                Recorded before the vendor list existed — see Vendors &amp; Transfers
                                below for the structured position.
                              </span>
                            )}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </SectionCard>
          )}

          {/* ── Human oversight (Art 14) ──
              Always rendered, never hidden behind "has content". An empty oversight block is
              the finding: the register defines `agentic-ai` as AI that acts without a human
              approving each step, and until now asked nothing about who could stop it. */}
          <SectionCard
            title="Human Oversight"
            icon="ri-user-star-line"
            // A count, not a verdict: "2 of 3" is deliberately info rather than success,
            // because whether two are enough depends on the system and is not this chip's call.
            // Zero is the one state that is a verdict.
            actions={
              hasAnyOversight(oversight)
                ? (
                  <span className="np-chip np-tone-info">
                    {oversightCount} of {HUMAN_OVERSIGHT_CONTROLS.length} controls
                  </span>
                )
                : <span className="np-chip np-tone-danger">No controls recorded</span>
            }
          >
            {oversightMissing && (
              <div className="np-banner np-tone-danger mb-4" role="alert">
                <i className="ri-error-warning-line"></i>
                <span>
                  <strong>A high-risk system with no oversight control recorded.</strong> Art 14
                  requires that a human can meaningfully intervene. The server will not let this
                  record be submitted or approved until at least one control below is in place and
                  an accountable person is named.
                </span>
              </div>
            )}

            <div className="np-list mb-4">
              {HUMAN_OVERSIGHT_CONTROLS.map((control) => (
                <div key={control.field} className="np-list-item" style={{ alignItems: 'flex-start' }}>
                  <div>
                    <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                      <YesNo value={!!oversight[control.field]} yesTone={control.tone} noTone="danger" />
                      <span className="fw-semibold" style={{ fontSize: '0.828rem' }}>
                        <i className={`${control.icon} me-1`}></i>
                        {control.label}
                      </span>
                    </div>
                    <p className="np-hint mb-0">{control.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="row g-4">
              <div className="col-12 col-md-6">
                <FieldLabel>Accountable person</FieldLabel>
                <div>{identity(personName, accountablePersonId, 'person')}</div>
                <p className="np-hint">A named individual from the People register, not a team.</p>
              </div>
              <div className="col-12 col-md-6">
                <LabelValue
                  label="Escalation path"
                  value={oversight.escalationPath ? <TextBlock value={oversight.escalationPath} /> : undefined}
                  hint="Who is told, and how, when oversight is actually exercised."
                />
              </div>
            </div>
          </SectionCard>

          {/* ── Vendors, models and transfers (FR-11) ──
              One card, because the two lists answer one question together: which models are
              involved, and where do they run. Hosting region is what turns a vendor row into a
              Chapter V transfer question. */}
          {(vendors.length > 0 || transfers.length > 0) && (
            <SectionCard title="Vendors & Transfers" icon="ri-shopping-bag-3-line">
              {unlawfulTransfer && (
                <div className="np-banner np-tone-danger mb-4" role="alert">
                  <i className="ri-alert-line"></i>
                  <span>
                    <strong>A transfer with no Chapter V mechanism recorded.</strong> This is the
                    most reportable state the record can be in, and it blocks activation
                    server-side — the same rule the Record of Processing Activities applies.
                  </span>
                </div>
              )}

              {vendors.length > 0 && (
                <>
                  <FieldLabel>Vendors &amp; models</FieldLabel>
                  <div className="np-table-wrap mb-4">
                    <table className="np-table">
                      <thead>
                        <tr>
                          <th>Vendor</th>
                          <th>Model</th>
                          <th>Hosting region</th>
                          <th>Trains on our data</th>
                          <th>DPA reference</th>
                          <th>Contract expiry</th>
                        </tr>
                      </thead>
                      <tbody>
                        {vendors.map((v) => (
                          <React.Fragment key={v.id}>
                            <tr>
                              <td className="np-row-title">{v.name || <Dash />}</td>
                              <td>{v.modelName || <Dash />}</td>
                              <td>{v.hostingRegion || <Dash />}</td>
                              {/* "Yes" is the answer that matters here, so it is the loud one —
                                  inverted against every other flag on the page on purpose. */}
                              <td><YesNo value={!!v.trainsOnOurData} yesTone="danger" noTone="success" /></td>
                              <td>{v.dpaReference || <Dash />}</td>
                              <td>
                                {v.contractExpiry
                                  ? <DueChip date={v.contractExpiry} pastLabel="expired" />
                                  : <Dash />}
                              </td>
                            </tr>
                            {v.subProcessors.trim() && (
                              <tr>
                                <td colSpan={6} className="np-hint">
                                  <i className="ri-corner-down-right-line me-1"></i>
                                  Sub-processors: {v.subProcessors}
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {transfers.length > 0 && (
                <>
                  <FieldLabel>Transfers outside the EEA</FieldLabel>
                  <div className="np-table-wrap">
                    <table className="np-table">
                      <thead>
                        <tr>
                          <th>Country</th>
                          <th>Recipient</th>
                          <th>Mechanism</th>
                          <th>Safeguards</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transfers.map((t) => {
                          const mechanism = (t.mechanism || 'none') as TransferMechanism;
                          return (
                            <tr key={t.id}>
                              <td className="np-row-title">{t.country || <Dash />}</td>
                              <td>{t.recipient || <Dash />}</td>
                              <td>
                                <span className={`np-chip np-tone-${TRANSFER_MECHANISM_TONE[mechanism]}`}>
                                  {TRANSFER_MECHANISM_LABELS[mechanism]}
                                </span>
                              </td>
                              <td>{t.safeguards || <Dash />}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </SectionCard>
          )}

          {/* ── Post-market monitoring and periodic review (FR-20 / FR-22) ── */}
          {hasMonitoringOrReview && (
            <SectionCard
              title="Monitoring & Review"
              icon="ri-radar-line"
              // Only when it has actually gone by. The date itself is a labelled field below,
              // so an always-on chip here would just say the same thing twice.
              actions={
                reviewDays !== null && reviewDays < 0
                  ? <span className="np-chip np-tone-danger"><i className="ri-alarm-warning-line"></i>Review overdue</span>
                  : undefined
              }
            >
              <div className="row g-4 mb-2">
                <div className="col-12 col-md-6">
                  <LabelValue
                    label="Review cadence"
                    value={uc.reviewFrequency ? REVIEW_FREQUENCY_LABELS[uc.reviewFrequency] : undefined}
                    hint="Nothing brought an active use case back around before this field existed."
                  />
                </div>
                <div className="col-12 col-md-6">
                  <FieldLabel>Next review</FieldLabel>
                  <div>
                    {uc.nextReviewDate
                      ? <DueChip date={uc.nextReviewDate} pastLabel="overdue" />
                      : <NotProvided label="Not scheduled" />}
                  </div>
                </div>
              </div>

              <hr className="np-divider" />

              {MONITORING_FIELDS.map(({ field, label, guidance }) => (
                <div key={field} className="mb-4">
                  <FieldLabel>{label}</FieldLabel>
                  <TextBlock value={monitoring[field] || ''} />
                  <p className="np-hint mb-0">{guidance}</p>
                </div>
              ))}
            </SectionCard>
          )}

          {/* ── The decision (NFR-01) ──
              Before this existed, "approval" was a status write with no actor and no timestamp —
              any user could click Mark Active and nothing recorded that they had. These four
              attributes are written only by the transition endpoint, from the caller's verified
              token, and are ignored if a client PUTs them. */}
          {decision && (
            <SectionCard
              title="Decision"
              icon={APPROVAL_DECISION_ICONS[decision]}
              actions={
                <span className={`np-chip np-tone-${APPROVAL_DECISION_TONE[decision]}`}>
                  <i className={APPROVAL_DECISION_ICONS[decision]}></i>
                  {APPROVAL_DECISION_LABELS[decision]}
                </span>
              }
            >
              <div className="row g-4">
                <div className="col-12 col-md-6">
                  <FieldLabel>Decided by</FieldLabel>
                  {/* A userId, resolved through the Users register. NOT the People register —
                      see the file header for what that mistake looks like on screen. */}
                  <div>{identity(deciderName, decidedBy, 'user')}</div>
                  <p className="np-hint">Recorded from the verified caller, not from the request body.</p>
                </div>
                <div className="col-12 col-md-6">
                  <LabelValue
                    label="Decided at"
                    value={uc.decidedAt
                      ? <span className="np-tabular">{formatDate(uc.decidedAt)}</span>
                      : undefined}
                  />
                </div>
                {uc.decisionConditions && (
                  <div className="col-12">
                    <LabelValue
                      label={decision === 'rejected' ? 'Reason for rejection' : 'Conditions attached'}
                      value={<TextBlock value={uc.decisionConditions} />}
                      hint={decision === 'rejected'
                        ? 'Cleared from the record when it is reopened for rework; the audit trail keeps the immutable copy.'
                        : 'These have to be discharged for the approval to hold.'}
                    />
                  </div>
                )}
              </div>
            </SectionCard>
          )}

        </div>
      </div>
      )}

      {activeTab === 'attachments' && (
        <div className="row">
          <DocumentManager entityType="ai_use_case" entityId={uc.useCaseId} />
        </div>
      )}

      {/* The platform-standard cross-register linking used by every other register. Replaces
          the source app's risk-only list, because this covers risks plus controls, issues,
          incidents, evidence and the rest. AI use cases are register-less, so artefactRegister
          is empty. */}
      {activeTab === 'artefacts' && (
        <div className="row">
          <LinkedArtefacts
            artefactId={uc.useCaseId}
            artefactType="ai_use_case"
            artefactRegister=""
          />
        </div>
      )}

      {/* Generic Review Assignment module (reviewAssignmentsApi - not owned by or specific to
          AI use cases) plus the Bedrock review pass. Any authenticated viewer can see this tab
          (the Overview tab already carries no role gate), but only a tenant admin can assign/
          reassign a reviewer or apply a drafted field, and only the current assignee (or an
          admin) can mark an assignment complete - enforced for real server-side; the buttons
          below are just hidden when they'd 403 anyway. */}
      {/* Re-homed into the same np-detail-layout rail the Overview tab uses: reviewer
          assignment, flagged questions and the compliance package are things a reviewer checks
          alongside the findings, not after scrolling past them. */}
      {activeTab === 'review' && (
        <div className="np-detail-layout">
          <div className="np-meta-rail" style={{ position: 'static' }}>
            <div className="np-card mb-3">
              <div className="np-card-head" style={{ flexWrap: 'wrap', rowGap: '0.5rem' }}>
                <h2><i className="ri-user-follow-line"></i> Reviewer</h2>
                {primaryAssignment ? (
                  <span className={`np-chip np-tone-${primaryAssignment.status === 'complete' ? 'success' : primaryAssignment.assignedToUserId ? 'primary' : 'warning'}`}>
                    {primaryAssignment.status === 'complete' ? 'Complete' : primaryAssignment.assignedToUserId ? `Assigned · ${nameForUserId(primaryAssignment.assignedToUserId)}` : 'Unassigned'}
                  </span>
                ) : (
                  <span className="np-chip np-tone-neutral">No assignment yet</span>
                )}
              </div>
              <div className="np-card-body">
                {isTenantAdmin && (
                  <div className="np-field mb-2">
                    <label>{primaryAssignment?.assignedToUserId ? 'Reassign to' : 'Assign to'}</label>
                    <select
                      className="np-select"
                      value={primaryAssignment?.assignedToUserId || ''}
                      onChange={(e) => assignMutation.mutate({
                        assignmentId: primaryAssignment?.assignmentId,
                        assignedToUserId: e.target.value,
                        create: { scope: 'primary' },
                      })}
                    >
                      <option value="">Choose a reviewer…</option>
                      {usersList.map((u) => (
                        <option key={u.userId} value={u.userId}>{u.name || u.email}</option>
                      ))}
                    </select>
                    <p className="np-hint mt-1 mb-0">Any user in the tenant can be assigned - not just admins or a fixed compliance team.</p>
                  </div>
                )}
                {primaryAssignment && primaryAssignment.status !== 'complete' && (isTenantAdmin || primaryAssignment.assignedToUserId === currentUserId) && (
                  <button
                    type="button"
                    className="np-btn np-btn-ghost np-btn-sm"
                    disabled={completeAssignmentMutation.isPending}
                    onClick={() => completeAssignmentMutation.mutate(primaryAssignment.assignmentId)}
                  >
                    <i className="ri-check-line"></i> Mark review complete
                  </button>
                )}
              </div>
            </div>

            {fieldAssignments.length > 0 && (
              <div className="np-card mb-3">
                <div className="np-card-head"><h2><i className="ri-flag-line"></i> Flagged Questions</h2></div>
                <div className="np-card-body p-0">
                  <table className="np-table">
                    <thead><tr><th>Field</th><th>Note</th><th>Assigned to</th><th>Status</th><th></th></tr></thead>
                    <tbody>
                      {fieldAssignments.map((a) => (
                        <tr key={a.assignmentId}>
                          <td>{a.fieldKey}</td>
                          <td>{a.note || <Dash />}</td>
                          <td>
                            {isTenantAdmin ? (
                              <select
                                className="np-select"
                                style={{ maxWidth: '14rem' }}
                                value={a.assignedToUserId || ''}
                                onChange={(e) => assignMutation.mutate({ assignmentId: a.assignmentId, assignedToUserId: e.target.value })}
                              >
                                <option value="">Unassigned</option>
                                {usersList.map((u) => (
                                  <option key={u.userId} value={u.userId}>{u.name || u.email}</option>
                                ))}
                              </select>
                            ) : (
                              a.assignedToUserId ? nameForUserId(a.assignedToUserId) : <Dash />
                            )}
                          </td>
                          <td>
                            <span className={`np-chip np-tone-${a.status === 'complete' ? 'success' : a.assignedToUserId ? 'primary' : 'warning'}`}>{a.status}</span>
                          </td>
                          <td>
                            {a.status !== 'complete' && (isTenantAdmin || a.assignedToUserId === currentUserId) && (
                              <button
                                type="button"
                                className="np-icon-btn"
                                title="Mark complete"
                                onClick={() => completeAssignmentMutation.mutate(a.assignmentId)}
                              >
                                <i className="ri-check-line"></i>
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Compliance Package - a permanent record of every artefact this use case's
                approval created or linked, replacing the flat "only shown if already set" rows
                that used to sit in the Overview tab's Details card. Those could only ever show
                success; this also shows "will happen on approval" and "failed to draft, retry"
                - states the old rows had no way to express at all. */}
            {/* Also shown for an approved use case with NO coverage verdict at all (approved via
                the reviewOverrideReason bypass with no review ever having completed) - hiding
                the card entirely in that state made "coverage was never checked" render
                identically to "nothing to draft", which is exactly the wrong thing to be
                ambiguous about in a compliance tool. */}
            {(review?.coverageVerdict || uc.linkedProcessingActivityId || uc.linkedSubprocessorId || uc.linkedDpiaId || uc.status === 'approved' || uc.status === 'active') && (
              <div className="np-card mb-3">
                <div className="np-card-head"><h2><i className="ri-archive-2-line"></i> Compliance Package</h2></div>
                <div className="np-card-body">
                  {(() => {
                    const coverage = review?.coverageVerdict;
                    const isApproved = uc.status === 'approved' || uc.status === 'active';
                    if (isApproved && !coverage) {
                      return (
                        <div className="np-banner np-tone-warning mb-0">
                          <i className="ri-error-warning-line"></i>
                          <span>
                            <strong>Coverage was never assessed.</strong> This use case was
                            approved without a completed AI review (see the Audit tab for the
                            override reason) - nothing has been checked against the Processing
                            Activity, Subprocessor or DPIA registers.
                          </span>
                        </div>
                      );
                    }
                    type RowStatus = 'linked' | 'pending' | 'failed' | 'flagged';
                    const row = (label: string, status: RowStatus, opts: { linkTo?: string; linkLabel?: string; detail?: string; extraChip?: React.ReactNode }) => {
                      const tone = status === 'linked' ? 'success' : status === 'failed' ? 'danger' : status === 'flagged' ? 'info' : 'neutral';
                      const chipLabel = status === 'linked' ? 'Linked' : status === 'failed' ? 'Failed to draft' : status === 'flagged' ? 'Flagged' : 'Pending on approval';
                      return (
                        <div key={label} className="d-flex align-items-start justify-content-between gap-2 py-2 border-bottom">
                          <div style={{ minWidth: 0 }}>
                            <div className="fw-semibold">{label}</div>
                            {opts.linkTo ? (
                              <Link to={opts.linkTo} className="np-tabular">{opts.linkLabel}</Link>
                            ) : (
                              <div className="np-sub">{opts.detail}</div>
                            )}
                          </div>
                          <div className="d-flex align-items-center gap-2 flex-shrink-0">
                            {opts.extraChip}
                            <span className={`np-chip np-tone-${tone}`}>{chipLabel}</span>
                            {status === 'failed' && (
                              <button
                                type="button"
                                className="np-btn np-btn-ghost np-btn-sm"
                                disabled={retryDraftingMutation.isPending}
                                onClick={() => retryDraftingMutation.mutate()}
                              >
                                <i className="ri-refresh-line"></i> Retry
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    };

                    const rows: React.ReactNode[] = [];
                    const pa = coverage?.processingActivity;
                    if (uc.linkedProcessingActivityId) {
                      rows.push(row('Processing Activity', 'linked', { linkTo: `/processing-activities/${uc.linkedProcessingActivityId}`, linkLabel: uc.linkedProcessingActivityId }));
                    } else if (pa?.covered || pa?.needsNew) {
                      rows.push(row('Processing Activity', isApproved ? 'failed' : 'pending', {
                        detail: pa.covered ? `Will link to existing ${pa.matchedId}` : 'Will create a new draft record',
                      }));
                    }
                    const sp = coverage?.subprocessor;
                    if (uc.linkedSubprocessorId) {
                      rows.push(row('Subprocessor', 'linked', { linkTo: `/subprocessors/${uc.linkedSubprocessorId}`, linkLabel: uc.linkedSubprocessorId }));
                    } else if (sp?.covered || sp?.needsNew) {
                      rows.push(row('Subprocessor', isApproved ? 'failed' : 'pending', {
                        detail: sp.covered ? `Will link to existing ${sp.matchedId}` : 'Will create a new draft record',
                      }));
                    }
                    if (uc.linkedDpiaId) {
                      rows.push(row('DPIA', 'linked', {
                        linkTo: `/documents/${uc.linkedDpiaId}`,
                        linkLabel: uc.linkedDpiaId,
                        extraChip: linkedDpiaResidualRisk ? (
                          <span className={`np-chip np-tone-${RESIDUAL_RISK_TONE[linkedDpiaResidualRisk]}`}>
                            Residual risk: {RESIDUAL_RISK_LABELS[linkedDpiaResidualRisk]}
                          </span>
                        ) : undefined,
                      }));
                    } else if (coverage?.dpiaNeeded) {
                      rows.push(row('DPIA', isApproved ? 'failed' : 'pending', { detail: 'Will create a draft DPIA' }));
                    }
                    if (coverage?.aiModuleCompliancePageRelevant) {
                      rows.push(row('AI Module Compliance Page', 'flagged', { detail: 'Customer-facing module - check the page directly, no per-record link exists here' }));
                    }
                    return rows.length > 0 ? (
                      <div className="mb-0">{rows}</div>
                    ) : (
                      <NotProvided label="Nothing to draft - existing coverage already accounts for this use case" />
                    );
                  })()}
                </div>
              </div>
            )}
          </div>

          <div>
            <div className="np-card mb-3">
              <div className="np-card-head">
                <h2><i className="ri-sparkling-2-line"></i> AI-Assisted Review</h2>
                {review && (
                  <span className={`np-chip np-tone-${review.status === 'complete' ? 'success' : review.status === 'failed' ? 'danger' : 'warning'}`}>{review.status}</span>
                )}
              </div>
              <div className="np-card-body">
                <p className="np-sub">
                  One pass over the submitted request, generated by Bedrock — advisory only, the
                  assigned reviewer makes the actual call.
                </p>
                {(!review || review.status === 'failed' || reviewStale) && (
                  <button
                    type="button"
                    className="np-btn np-btn-ghost np-btn-sm"
                    disabled={startReviewMutation.isPending}
                    onClick={() => startReviewMutation.mutate()}
                  >
                    <i className="ri-refresh-line"></i> {!review ? 'Run AI review' : reviewStale ? 'Re-run review' : 'Retry review'}
                  </button>
                )}
                {review?.status === 'generating' && (
                  <div className="d-flex align-items-center gap-2">
                    <div className="spinner-border spinner-border-sm text-primary" role="status" />
                    <span className="np-sub">Reviewing…</span>
                  </div>
                )}
                {review?.status === 'failed' && (
                  <div className="np-banner np-tone-danger mb-0 mt-3">
                    <i className="ri-error-warning-line"></i>
                    <span>{review.error || 'The review failed for an unknown reason.'}</span>
                  </div>
                )}
                {reviewStale && (
                  <div className="np-banner np-tone-warning mb-0 mt-3">
                    <i className="ri-time-line"></i>
                    <span>
                      This review is out of date — the use case has changed since it ran.
                      {uc.status === 'approved' || uc.status === 'active'
                        ? ' A stale review also blocks the Compliance Package from drafting or retrying — re-run it to unblock that.'
                        : ' Re-run it before approving.'}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {review?.status === 'complete' && (review.precedentMatches?.length ?? 0) > 0 && (
              <SectionCard title="Precedent & Duplication" icon="ri-git-repository-line">
                <div className="np-list">
                  {review.precedentMatches?.map((m, i) => (
                    <div key={i} className="np-list-item">
                      <span className="fw-semibold">{m.name}</span> — {m.why}
                    </div>
                  ))}
                </div>
              </SectionCard>
            )}

            {review?.status === 'complete' && (review.piiDisagreements?.length ?? 0) > 0 && (
              <SectionCard title="Data Sensitivity Disagreements" icon="ri-eye-line">
                <div className="np-banner np-tone-warning mb-0">
                  <i className="ri-eye-line"></i>
                  <div>
                    <strong>Where Bedrock disagrees with the submitter</strong>
                    <ul className="mb-0">
                      {review.piiDisagreements?.map((d, i) => (
                        <li key={i}><strong>{d.dataFieldName}</strong> — {d.why}</li>
                      ))}
                    </ul>
                  </div>
                </div>
                {(review.inferredDataGaps?.length ?? 0) > 0 && (
                  <div className="mt-3">
                    <strong>Data inferred from the whole request</strong>
                    <ul className="mb-0">
                      {review.inferredDataGaps?.map((g, i) => (
                        <li key={i}><strong>{g.dataUsage}</strong> — {g.why}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </SectionCard>
            )}

            {review?.status === 'complete' && (review.inferredDataGaps?.length ?? 0) > 0 && (review.piiDisagreements?.length ?? 0) === 0 && (
              <SectionCard title="Data Inferred From The Request" icon="ri-eye-line">
                <ul className="mb-0">
                  {review.inferredDataGaps?.map((g, i) => (
                    <li key={i}><strong>{g.dataUsage}</strong> — {g.why}</li>
                  ))}
                </ul>
              </SectionCard>
            )}

            {review?.status === 'complete' && (review.keyRisks?.length ?? 0) > 0 && (
              <SectionCard title="Risks Worth Raising" icon="ri-alarm-warning-line">
                <div className="np-banner np-tone-danger mb-0">
                  <i className="ri-alarm-warning-line"></i>
                  <div>
                    <ul className="mb-0">
                      {review.keyRisks?.map((r, i) => (
                        <li key={i}><strong>{r.risk}</strong>{r.why ? ` — ${r.why}` : ''}</li>
                      ))}
                    </ul>
                    <p className="np-hint mt-1 mb-0">Advisory only — raise any of these through the AI Risk Assessment panel on the Overview tab if they hold up.</p>
                  </div>
                </div>
              </SectionCard>
            )}

            {review?.status === 'complete' && review.draftedFields && Object.keys(review.draftedFields).length > 0 && (
              <SectionCard title="Auto-Filled Fields" icon="ri-magic-line">
                {Object.entries(review.draftedFields).map(([fieldName, entry]) => (
                  // align-items-start (not -center) + no flex-wrap: a long value/rationale
                  // wraps within its own column, but the "Applied" chip stays pinned to
                  // the same row instead of dropping onto its own line and floating
                  // mid-block once the row grows taller than one line.
                  <div key={fieldName} className="d-flex align-items-start justify-content-between gap-2 py-2 border-bottom">
                    <div style={{ minWidth: 0 }}>
                      <div className="fw-semibold">{DRAFTED_FIELD_LABELS[fieldName] || fieldName}</div>
                      <div className="np-sub">{String(entry.value)} — {entry.rationale}</div>
                    </div>
                    <span className="np-chip np-tone-success flex-shrink-0"><i className="ri-check-line"></i> Applied</span>
                  </div>
                ))}
                <p className="np-hint mt-2 mb-0">Already written to the record above — correct any of it directly in the full intake wizard if it's wrong.</p>
              </SectionCard>
            )}

            {review?.status === 'complete' && (review.humanInvestigationChecklist?.length ?? 0) > 0 && (
              <SectionCard title="Needs Human Follow-Up" icon="ri-flag-line">
                <ul className="mb-0">
                  {review.humanInvestigationChecklist?.map((c, i) => (
                    <li key={i}>{c}</li>
                  ))}
                </ul>
              </SectionCard>
            )}

            <div className="np-card">
              <div className="np-card-body">
                <Link to={`/ai-use-cases/${uc.useCaseId}/intake/1`} className="np-btn np-btn-primary np-btn-sm">
                  <i className="ri-edit-2-line"></i> Complete in full intake wizard
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Audit tab - the events log_audit() writes on every transition, drafting failure/
          retry and review override, rendered for the first time. */}
      {activeTab === 'audit' && (
        <div className="np-card">
          <div className="np-card-head"><h2><i className="ri-history-line"></i> Audit Trail</h2></div>
          <div className="np-card-body p-0">
            {auditLoading ? (
              <div className="d-flex justify-content-center py-5">
                <div className="spinner-border spinner-border-sm text-primary" role="status" />
              </div>
            ) : auditEvents.length === 0 ? (
              <div className="p-4"><NotProvided label="No audit events recorded yet" /></div>
            ) : (
              <table className="np-table">
                <thead><tr><th style={{ width: '160px' }}>When</th><th style={{ width: '200px' }}>Event</th><th style={{ width: '160px' }}>By</th><th>Details</th></tr></thead>
                <tbody>
                  {auditEvents.map((ev) => (
                    <tr key={ev.auditId}>
                      <td className="np-tabular np-sub">{formatDate(ev.timestamp)}</td>
                      <td><span className="np-chip np-tone-neutral">{ev.eventType}</span></td>
                      <td>{nameForUserId(ev.userId) || ev.userId}</td>
                      <td className="np-sub">{describeAuditEvent(ev)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ── The decision form ──
          Not a confirm dialog: `swal.confirm(title, text)` returns a boolean and has no prompt
          variant, so it cannot carry the reason the API requires on a rejection or the
          conditions that turn an approval into an approval-with-conditions. */}
      {decisionTarget && decisionForm && (
        <NimbusModal centered>
          <NimbusModalContent
            title={<><i className={`${decisionForm.icon} me-2`}></i>{decisionForm.title}</>}
            onClose={() => setDecisionTarget(null)}
            footer={
              <>
                <button
                  type="button"
                  className="np-btn np-btn-ghost"
                  onClick={() => setDecisionTarget(null)}
                  disabled={transitionMutation.isPending}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={decisionForm.submitClass}
                  onClick={() => transitionMutation.mutate({
                    status: decisionTarget,
                    text: decisionText,
                    reviewOverrideReason: needsReviewOverride ? overrideReasonText : undefined,
                  })}
                  disabled={transitionMutation.isPending || !decisionTextReady}
                >
                  {transitionMutation.isPending
                    ? <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                    : <i className={TRANSITION_ICON[decisionTarget]}></i>}
                  {transitionMutation.isPending ? AI_STATUS_PENDING_LABELS[decisionTarget] : decisionForm.submitLabel}
                </button>
              </>
            }
          >
              <p className="np-sub">{decisionForm.intro}</p>

              {/* Approving drafts the DPIA/Processing Activity/Subprocessor documents via
                  Bedrock before the request returns, which commonly takes 30-70s — long enough
                  that a plain disabled button reads as broken. Confirmed live: this exact
                  request took over a minute for AI-048. Say so explicitly while it runs. */}
              {decisionTarget === 'approved' && transitionMutation.isPending && (
                <div className="np-banner np-tone-info mb-3">
                  <span className="spinner-border spinner-border-sm" role="status"></span>
                  <div>
                    <strong>Drafting compliance documents…</strong> This can take up to a
                    minute — please leave this window open until it finishes.
                  </div>
                </div>
              )}

              {/* Confirmation drawer: on Approve, name every consequence before it happens
                  rather than after — the DPO confirms this exact list, not a generic dialog. */}
              {decisionTarget === 'approved' && !transitionMutation.isPending && (
                <div className={`np-banner np-tone-${reviewCurrent ? 'info' : 'warning'} mb-3`}>
                  <i className={reviewCurrent ? 'ri-git-merge-line' : 'ri-time-line'}></i>
                  <div>
                    {reviewCurrent ? (
                      approvalConsequences.length > 0 ? (
                        <>
                          <strong>This approval will:</strong>
                          <ul className="mb-0">
                            {approvalConsequences.map((line, i) => <li key={i}>{line}</li>)}
                          </ul>
                        </>
                      ) : (
                        <strong>Nothing further will happen — existing coverage already accounts for this use case.</strong>
                      )
                    ) : reviewStale ? (
                      <strong>The latest AI review is out of date — this use case was edited since it ran.</strong>
                    ) : (
                      <strong>No completed AI review exists for this use case.</strong>
                    )}
                  </div>
                </div>
              )}

              {needsReviewOverride && (
                <div className="np-field mb-3">
                  <label htmlFor="ai-review-override">Reason for approving without a current review <span className="text-danger">*</span></label>
                  <textarea
                    id="ai-review-override"
                    className="np-textarea"
                    rows={3}
                    value={overrideReasonText}
                    placeholder="e.g. the AI spend cap was reached and a review could not be generated - approved on manual assessment instead."
                    onChange={(e) => setOverrideReasonText(e.target.value)}
                  />
                  <div className="np-hint">Required, and recorded in the audit trail as an override — this bypasses the normal review gate.</div>
                </div>
              )}

              <div className="np-field mb-0">
                <label htmlFor="ai-decision-text">{decisionForm.label}</label>
                <textarea
                  id="ai-decision-text"
                  className="np-textarea"
                  rows={5}
                  value={decisionText}
                  placeholder={decisionForm.placeholder}
                  onChange={(e) => setDecisionText(e.target.value)}
                />
                <div className="np-hint">{decisionForm.hint}</div>
              </div>
          </NimbusModalContent>
        </NimbusModal>
      )}
    </div>
  );
};

export default AiUseCaseDetail;
